<template>
  <div class='d-flex'>

    <v-btn icon large class='mt-n2 mr-1' :color="showUnrouted ? 'warning' : 'grey lighten-1'" @click="$emit('toggleUnrouted')">
      <v-icon>{{ showUnrouted ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
    </v-btn>

    <v-btn icon large class='mt-n2 mx-n2' :class='animateHelp ? "bounce" : ""' @click='openHelp'>
      <v-icon :color='animateHelp ? "primary" : "grey lighten-1"'>
        mdi-help-circle-outline
      </v-icon>
    </v-btn>

    <v-tooltip top open-delay='500'>
      <template v-slot:activator='{ on }'>
        <v-btn icon large class='mt-n2 mr-1' v-on='on' @click="$emit('openProgress')">
          <v-icon color='grey lighten-1'>
            mdi-progress-check
          </v-icon>
        </v-btn>
      </template>
      <span>Route Progress</span>
    </v-tooltip>

    <v-tooltip top open-delay='700' :disabled='selectedProjects'>
      <template v-slot:activator='{ on }'>
        <div v-on='on' class='d-inline-block'>
          <v-btn
            class='ml-4'
            width='150px'
            small
            color='error'
            elevation='0'
            @click="deselectAll"
            :disabled='!selectedProjects'
          >
            Clear selected
          </v-btn>
        </div>
      </template>
      <span>No projects selected</span>
    </v-tooltip>

    <v-btn
      class='ml-4'
      @mouseover='hasMouseOver = true'
      @mouseleave='hasMouseOver = false'
      small
      width='150px'
      outlined
      :color='buttonColor'
      @click='addGroup'
      dark
    >
      Add New Group
    </v-btn>

    <v-tooltip top open-delay='500'>
      <template v-slot:activator='{ on }'>
        <v-btn
          icon
          large
          class='mt-n2 ml-2'
          v-on='on'
          color='grey'
          :disabled='loading'
          @click="$emit('toggleShowAllProjects')"
        >
          <v-icon size='20'>{{ showAllProjects ? 'mdi-arrow-collapse-up' : 'mdi-arrow-expand-down'}}</v-icon>
        </v-btn>
      </template>
      <span class='caption'>
        {{ (showAllProjects ? 'Collapse' : 'Expand') + ' all Groups' + (showAllProjects ? '' : ' (this could take a while)') }}
      </span>
    </v-tooltip>

    <v-tooltip top :open-delay='filterLawnCare ? "200" : "700"' :disabled='selectedProjects'>
      <template v-slot:activator='{ on }'>
        <div v-on='on' class='d-inline-block'>
        <v-btn
          v-if='division.division === "Lawn"'
          class='mt-n2 mx-2'
          :class='animateLawnCareButton ? " shake" : "" '
          icon
          large
          @click='toggleFilterLawnCare'
        >
        <v-icon :color='filterLawnCare ? "error" : "grey lighten-1"'>
          mdi-mower-bag
        </v-icon>
        </v-btn>
        </div>
      </template>
      <span v-if='!filterLawnCare'>Click to show Lawn Care projects only (will disable selection)</span>
      <span v-else>LawnCare Filter is active (selection is disabled) Click to show all projects</span>
    </v-tooltip>

    <v-spacer></v-spacer>
  
    <v-btn
      v-if='showMap'
      class='mr-4'
      elevation='0'
      small
      width='150px'
      color='info'
      @click='clearMarkers'
      :disabled='disableClearMarkers'
    >
      Clear Markers
    </v-btn>

    <v-tooltip top open-delay='900'>
      <template v-slot:activator='{ on }'>
        <v-btn
          class='mr-4'
          v-on='on'
          width='150px'
          small
          color="info"
          elevation='0'
          @click="toggleMap"
        >
          {{ showMap ? 'Hide ' : 'Show ' }} Map
        </v-btn>
      </template>
      <span>{{ showMap ? "Out of the way, map!" : "Need a Map?"}}</span>
    </v-tooltip>

    <wc-dialog
      :showDialog='showHelpDialog'
      title='Project Routing Page Help'
      icon='mdi-help-circle-outline'
      width='800'
      hideCancel
      saveText='Got it'
      @save='showHelpDialog = false'
    >
      <v-card-text v-if='division.division === "Irrigation"'>
        Adding or moving a project within the group, will automatically re-number all projects in that group.<br><br>
        Projects that have their group set (from either the estimate page or the project page) but have no number set will appear at the top of that group (they're not counted in the order) and will be in holding (and shaded red) until you place them into the list.<br><br>
        Selecting and moving projects works exactly like the schedule. However, projects cannot be 'unrouted' here.<br><br>  
        Using the map button <v-icon small>mdi-map-marker</v-icon>will add the pins for that group onto the map.<br>
        Hover over a marker to show the project name and the address.<br>
        If you change the order for a group, you'll need to clear the markers, and re-add the pins to see your changes reflected.<br>
        You can show pins for multiple groups, but currently they'll all be the same color.<br>
        We're hoping to implement additional features to this page (when time permits).
      </v-card-text>

      <v-card-text v-if='setupHelp && division.division === "Irrigation"'>
        <p class='primary--text body-1 font-weight-bold'>Y setup</p>
        <p class='primary--text'>To keep functionality consistent, instead of grouping Y into different groups based on the hundreds digit, we're allowing a second letter to be added to the group, so that each grouping in Y can have its own group.  So if desired, Y 100-199 can become YA 1-99, Y200-299 can become YB 1-99, and so on... (or maybe YF for Floating stone, YC for Camrose, YP for Pigeon Lake? It's up to you!)</p>
        <p class='primary--text'>Please note that when you move even one project into/out of/within Y the remaining projects will be renumbered and they'll lose their 100's digit and be renumbered from 1 - 35. (you might want to record the current grouping values before you start)</p>
      </v-card-text>

      <v-card-text v-if='setupHelp && division.division === "Snow"'>
        <p class='primary--text body-1 font-weight-bold'>Welcome to the new Snow Routing/Schedule Page</p>
        <p class='primary--text'>Similar to the lawn routing page, this area serves as BOTH the routing page (where you can set the order/group of each snow project) AND the main schedule page (where you can see if/when the field crews were last out to a project, or how they're doing on a particular snow day).</p>
        <p class='primary--text'>Regular snow shovelling no longer requires work orders to be generated and completed each snow day, but rather Crew Visits are recorded each time the site is cleared.
        <br><br><b>**Please note** - Crew Visits should only include work that's included in the regular contract.</b>
        <br>If additional billable service (ie - additional rock chip, scraping) has been requested by the customer and an additional invoice needs to be sent, then a work order should be created.  This will be added to the Snow Service schedule and handled very similarly to an irrigation service work order. (it will need to be processed thorugh the Work Order Ready for Office page for the new invoice to be generated.)</p>
        <p class='primary--text'><i>We're still working out some of the details... but the system will be ready for when the snow season starts</i></p>
      </v-card-text>

      <v-card-text>
        <p class='font-weight-bold body-2'>Important</p>
        <p v-if='division.division === "Lawn" || division.division === "Snow"'>In general, you do not want to see unrouted projects here.  Projects that are either in un-routed, or are undisplayed (if there's an error at the top stating so) will NOT be seen by the fieldcrews.  They need to be routed before they will appear.  When a new project sells, you'll want to check this page to make sure those new projects are routed correctly.  By selecting a project and then moving it (either within a group or into a new group) its route group letter and route number will be automatically be updated based on its new position.</p>
        <p v-else>In general, you do not want to see unrouted projects here.  When a new project sells, you'll want to check this page to make sure those new projects are routed correctly.</p>
        <p>Projects that are shaded in red need to be added into a group (select them, and insert them into a group either before another project using the red arrow or to the end of the group by clicking "add to end" which will appear the bottom of any group when projects are selected)</p>
        <p class='success--text'>Clicking the no-geolocation warning <v-icon small color='green'>mdi-map-marker-question</v-icon> will automatically save new coordinates directly into that project (based off Google's search for that address).  A marker will be placed on the map so you can confirm the location (you may have to pan/zoom if the marker falls outside the current map bounds).  If this location on the map is not correct, please open the project page and update the latitude and longitude manually (try using maps.edmonton.ca address lookup for Edmonton addresses - lat/lng is listed within the Assessment tab at the bottom after you've done a search by address.  Or, for locations outside Edmonton - drop a pin using Google maps, if you click on that pin to show its coordinates) </p>
        <div class='mx-2 mt-4 grey--text font-italic'>
          <div class='caption mt-n1'>hold shift and use the mousewheel to scroll horizontally across groups</div>
          <div class='caption mt-n1'>use the mousewheel within a group to scroll vertically</div>
        </div>
      </v-card-text>
     
    </wc-dialog>

  </div>
</template>

<script>

export default {
  name: 'route-groups-button-bar',
  props: {
    selected: {
      required: true
    },
    showMap: {
      required: true
    },
    disableClearMarkers: {
      required: true
    },
    division: {
      required: false,
    },
    animateHelp: {
      required: false,
      type: Boolean,
      default: false
    },
    setupHelp: {
      required: false,
      type: Boolean,
      default: false
    },
    filterLawnCare: {
      required: false,
      type: Boolean,
      default: false
    },
    showAllProjects: {
      required: false,
      type: Boolean,
      default: false
    },
    loading: {
      required: false,
      type: Boolean,
      default: false
    },
    showUnrouted: {
      required: false,
      type: Boolean,
      default: true
    }
  },
  data: () => ({
    hasMouseOver: false,
    showHelpDialog: false,
    animateLawnCareButton: false,
  }),
  computed: {
    selectedProjects () {
      return this.selected.length > 0
    },
    buttonColor () {
      return this.hasMouseOver ? 'info' : 'grey lighten-1'
    },
  },
  methods: {
    deselectAll () {
      this.$emit('deselectAll')
    },
    toggleMap () {
      this.$emit('toggleMap')
    },
    toggleFilterLawnCare () {
      this.$emit('toggleFilterLawnCare')
    },
    addGroup () {
      this.$emit('addGroup')
    },
    clearMarkers () {
      this.$emit('clearMarkers')
    },
    openHelp () {
      this.showHelpDialog = true
      this.$emit('helpClicked')
    },
    animateFilterLawnCareButton () {
      this.animateLawnCareButton = true
      setTimeout(() => {
        this.animateLawnCareButton = false
      }, 820)
    }
  }
}
</script>

<style scoped>

.bounce { animation: bounce 2s linear 2s infinite forwards; }

@keyframes bounce {
    0% { transform: scale(1.0); }
   15% { transform: rotate(20deg) scale(1.2); }
   30% { transform: rotate(-20deg) scale(1.6); }
   45% { transform: rotate( 0deg) scale(1.0); }
  100% { transform: scale(1.0); }
}

.shake {
  animation: shake 0.82s cubic-bezier(.36,.07,.19,.97) both;
  transform: translate(0, 0);
}

@keyframes shake {
  10%, 90% {
    transform: translate(-1px, 0);
  }
  
  20%, 80% {
    transform: translate(2px, 0);
  }

  30%, 50%, 70% {
    transform: translate(-4px, 0);
  }

  40%, 60% {
    transform: translate(4px, 0);
  }
}

</style>